'use client';

import { FunctionalIcon } from '@/app/Components/FunctionalIcon';
import React, { useEffect, useMemo, useState } from 'react';
import ScheduleView from '@/app/Components/ScheduleView';
import type { MeetingTime, ScheduledCourse } from '@/app/types/interfaces';
import { formatBaseIdDisplay } from '@/app/lib/utils';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';

interface ScheduleResultsRendererProps {
  data?: any;
  metadata?: any;
  onSaveSchedule?: (scheduleIndex: number, courses: any[]) => void;
  onDiscardDraft?: () => void;
  showCreditLoadStatus?: boolean;
}

const parseBaseId = (baseId?: string) => {
  if (!baseId) return { department: '000', classNumber: '000' };
  const digits = baseId.replace(/\D/g, '');
  if (digits.length >= 8) {
    return {
      department: digits.slice(2, 5),
      classNumber: digits.slice(5, 8),
    };
  }
  return { department: baseId, classNumber: '000' };
};

const formatTimeLabel = (minutes?: number) => {
  if (minutes == null || Number.isNaN(minutes)) return 'TBA';
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 === 0 ? 12 : hours % 12;
  return `${displayHours}:${String(mins).padStart(2, '0')} ${period}`;
};

const getPrimaryMeeting = (meetingTimes: MeetingTime[] = []) => {
  if (meetingTimes.length === 0) return null;
  return meetingTimes[0];
};

const normalizeMeetingTimes = (meetingTimes: any[] = []): MeetingTime[] => {
  return meetingTimes
    .map((mt) => ({
      day: mt.day || 'TBA',
      startMinute: typeof mt.startMinute === 'number' ? mt.startMinute : 0,
      endMinute: typeof mt.endMinute === 'number' ? mt.endMinute : 0,
      location: mt.location,
      mode: mt.mode || 'in-person',
      campus: mt.campus,
      campusAbbrev: mt.campusAbbrev,
    }))
    .filter((mt) => mt.day && typeof mt.startMinute === 'number');
};

const normalizeCourse = (course: any): ScheduledCourse => {
  const baseId = course.baseId || course.base_id || course.id?.split(':')[0] || '';
  const { department, classNumber } = parseBaseId(baseId);
  const meetingTimes = normalizeMeetingTimes(course.meetingTimes || course.meetings || []);
  const primaryMeeting = getPrimaryMeeting(meetingTimes);

  return {
    id: course.id || `${baseId}:${course.section || course.sectionNumber || ''}`,
    baseId,
    name: course.courseName || course.name || course.title || baseId,
    campus: course.campus || primaryMeeting?.campus || 'NB',
    campuses: course.campuses || (primaryMeeting?.campus ? [primaryMeeting.campus] : undefined),
    day: primaryMeeting?.day || 'TBA',
    time: primaryMeeting
      ? `${formatTimeLabel(primaryMeeting.startMinute)}-${formatTimeLabel(primaryMeeting.endMinute)}`
      : 'TBA',
    section: course.section || course.sectionNumber || 'TBA',
    instructors: course.instructors || course.instructor || [],
    meetingTimes,
    class_number: course.class_number || course.classNumber || classNumber,
    department: course.department || department,
    location: primaryMeeting?.location || course.location || 'TBA',
    credits: course.credits,
    coreRequirements: course.coreRequirements || [],
  };
};

export function ScheduleResultsRenderer({ data, metadata, onSaveSchedule, onDiscardDraft, showCreditLoadStatus = true }: ScheduleResultsRendererProps) {
  const schedules = useMemo(() => {
    if (!data) return [];
    if (Array.isArray(data.schedules)) return data.schedules;
    if (data.schedule) return [data.schedule];
    return [];
  }, [data]);

  const [activeIndex, setActiveIndex] = useState(0);
  const [selectedCourseId, setSelectedCourseId] = useState<string | null>(null);

  const activeSchedule = schedules[activeIndex] || null;
  const normalizedCourses = useMemo(() => {
    if (!activeSchedule?.courses) return [];
    return activeSchedule.courses.map(normalizeCourse);
  }, [activeSchedule]);

  const totalSchedules = schedules.length;
  const totalFound = data?.totalFound ?? totalSchedules;
  const conflicts = Array.isArray(activeSchedule?.conflicts) ? activeSchedule.conflicts : [];
  const selectedCourse = normalizedCourses.find((course: ScheduledCourse) => course.id === selectedCourseId) || normalizedCourses[0] || null;

  const totalCredits = normalizedCourses.reduce((sum: number, course: ScheduledCourse) => {
    const credits = typeof course.credits === 'string' ? parseFloat(course.credits) : course.credits;
    return sum + (credits || 0);
  }, 0);
  const hasCreditData = normalizedCourses.some((course: ScheduledCourse) => course.credits !== undefined && course.credits !== null && course.credits !== '');

  const handlePrev = () => setActiveIndex((prev) => Math.max(0, prev - 1));
  const handleNext = () => setActiveIndex((prev) => Math.min(totalSchedules - 1, prev + 1));

  useEffect(() => {
    if (normalizedCourses.length === 0) {
      setSelectedCourseId(null);
      return;
    }

    setSelectedCourseId((current) =>
      current && normalizedCourses.some((course: ScheduledCourse) => course.id === current)
        ? current
        : normalizedCourses[0].id
    );
  }, [normalizedCourses, activeIndex]);

  const handleSave = (index: number) => {
    const schedule = schedules[index];
    if (!schedule) return;
    onSaveSchedule?.(index, schedule.courses || []);
  };

  if (totalSchedules === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center text-center text-[var(--ss-text-muted)]">
        <div className="mb-3 flex size-14 items-center justify-center rounded-card bg-[var(--ss-surface-subtle)]">
          <span className="text-xl"><FunctionalIcon name="calendar" /></span>
        </div>
        <p className="text-sm">No schedules to show yet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {(data?.status === 'draft' || metadata?.draft) && (
        <div className="rounded-card border border-[var(--ss-warning)] bg-[var(--ss-warning-bg)] px-4 py-3 text-sm text-[var(--ss-text)]">
          Draft schedules — Save keeps one on your account; Discard closes without saving.
        </div>
      )}
      {(onSaveSchedule || onDiscardDraft) ? <div className="flex flex-wrap items-center justify-between gap-3 rounded-card border border-[var(--ss-border)] bg-[var(--ss-surface)] p-4 shadow-e1">
        <div>
          <h3 className="text-lg font-semibold text-[var(--ss-text)]">
            Schedule {activeIndex + 1} of {totalSchedules}
          </h3>
          <p className="text-xs text-[var(--ss-text-muted)]">
            Found {totalFound} total options · {normalizedCourses.length} courses · {hasCreditData ? `${totalCredits} credits` : 'credits unavailable'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {onDiscardDraft && (
            <button
              type="button"
              onClick={onDiscardDraft}
              className="inline-flex min-h-12 items-center gap-2 rounded-control border border-[var(--ss-border)] px-3 text-sm font-medium text-[var(--ss-text)] hover:bg-[var(--ss-surface-subtle)]"
            >
              Discard
            </button>
          )}
          {onSaveSchedule && (
            <button
              onClick={() => handleSave(activeIndex)}
              className="inline-flex min-h-12 items-center gap-2 rounded-control bg-[var(--ss-brand)] px-3 text-sm font-semibold text-white shadow-e1 hover:bg-[var(--ss-brand-strong)]"
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
              Save
            </button>
          )}
        </div>
      </div> : null}

      <ScheduleView
        schedule={normalizedCourses}
        onNextSchedule={handleNext}
        onPrevSchedule={handlePrev}
        currentScheduleIndex={activeIndex}
        totalSchedules={totalSchedules}
        totalSchedulesGenerated={totalFound}
        currentScheduleScore={activeSchedule?.score}
        conflicts={conflicts}
        semester={metadata?.semester}
        onSaveSchedule={onSaveSchedule ? handleSave : undefined}
        showCreditLoadStatus={showCreditLoadStatus && hasCreditData}
      />

      <div className="rounded-card border border-[var(--ss-border)] bg-[var(--ss-surface)] p-4">
        <h4 className="mb-3 text-sm font-semibold text-[var(--ss-text)]">Courses in this schedule</h4>
        <div className="grid gap-2 sm:grid-cols-2">
          {normalizedCourses.map((course: ScheduledCourse) => (
            <button
              key={course.id}
              type="button"
              onClick={() => setSelectedCourseId(course.id)}
              className={`text-left rounded-control border p-3 transition-all ${selectedCourse?.id === course.id
                ? 'border-[var(--ss-brand)] bg-[var(--ss-brand-bg)] ring-2 ring-[var(--ss-focus)]'
                : 'border-[var(--ss-border)] bg-[var(--ss-surface-subtle)] hover:border-[var(--ss-brand)]'
                }`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-semibold text-[var(--ss-brand)]">
                  {formatBaseIdDisplay(course.baseId)}
                </span>
                <span className="text-[10px] uppercase text-[var(--ss-text-muted)]">Section {course.section}</span>
              </div>
              <div className="mt-1 line-clamp-1 text-sm font-medium text-[var(--ss-text)]">
                {course.name}
              </div>
              <div className="mt-1 text-xs text-[var(--ss-text-muted)]">
                {course.meetingTimes.length > 0
                  ? course.meetingTimes
                      .map((mt) => `${mt.day} ${formatTimeLabel(mt.startMinute)}-${formatTimeLabel(mt.endMinute)}`)
                      .join(', ')
                  : 'Times TBA'}
              </div>
            </button>
          ))}
        </div>

        {selectedCourse && (
          <div className="mt-4 rounded-card border border-[var(--ss-brand)] bg-[var(--ss-brand-bg)] p-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-[var(--ss-brand)]">
                  Selected Class
                </p>
                <h5 className="mt-1 text-base font-semibold text-[var(--ss-text)]">
                  {selectedCourse.name}
                </h5>
                <p className="mt-1 text-sm text-[var(--ss-text-muted)]">
                  {formatBaseIdDisplay(selectedCourse.baseId)} · Section {selectedCourse.section}
                </p>
              </div>
              <div className="text-right text-xs text-[var(--ss-text-muted)]">
                <div>{selectedCourse.credits || 'TBA'} credits</div>
                <div className="mt-1">{selectedCourse.campus || 'Campus TBA'}</div>
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2 mt-4 text-sm">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1">Meeting Times</p>
                <div className="space-y-1 text-slate-700 dark:text-slate-200">
                  {selectedCourse.meetingTimes.length > 0 ? selectedCourse.meetingTimes.map((meeting: MeetingTime, index: number) => (
                    <div key={`${selectedCourse.id}-meeting-${index}`}>
                      {meeting.day} {formatTimeLabel(meeting.startMinute)}-{formatTimeLabel(meeting.endMinute)}
                      {meeting.location ? ` · ${meeting.location}` : ''}
                    </div>
                  )) : (
                    <div>TBA</div>
                  )}
                </div>
              </div>

              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1">Instructor & Campus</p>
                <div className="space-y-1 text-slate-700 dark:text-slate-200">
                  <div>
                    {Array.isArray(selectedCourse.instructors) && selectedCourse.instructors.length > 0
                      ? selectedCourse.instructors.map((instructor: string | { name?: string }) => typeof instructor === 'string' ? instructor : instructor.name).join(', ')
                      : 'TBA'}
                  </div>
                  <div>{selectedCourse.location || 'Location TBA'}</div>
                  <div>{selectedCourse.campuses?.join(', ') || selectedCourse.campus || 'Campus TBA'}</div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
